'use client';

import type { RefObject } from 'react';
import { useCallback } from 'react';
import type {
  DigitalConsoleType,
  OzonetelCallStatus,
  OzonetelInitializePayload,
  OzonetelStartCallPayload,
} from '../../consts/ozonetel/sdk';
import { OZONE_TEL_MANUAL_CALL_UUI, OZONE_TEL_RELEASE_AGENT_MODE } from '../../consts/ozonetel/sdk';
import type { OztPhoneNumberPayload, OztSetModePayload } from '../../consts/ozonetel/shared';
import { getCallRecordingData } from '../../utils/getCallRecordingData';
import { getOzonetelSdk } from '../../utils/ozonetel/getSdk';
import type { OzonetelSipReadiness } from './useSipReadiness';

interface UseOzonetelSdkActionsParams {
  callStatusRef: RefObject<OzonetelCallStatus>;
  isInitializedRef: RefObject<boolean>;
  pendingPayloadRef: RefObject<OzonetelInitializePayload | undefined>;
  setCallStatus: (status: OzonetelCallStatus) => void;
  getInitializedSdk: () => Promise<DigitalConsoleType>;
  prepareAgentAudioForOutboundCall: () => Promise<void>;
  releaseAgentAudio: () => void;
  assertSipReady: OzonetelSipReadiness['assertSipReady'];
}

function getCallerId() {
  const callerId = getOzonetelSdk()?.getCallData?.().callerId;
  if (!callerId) {
    throw new Error('Ozonetel callerId is not available');
  }
  return callerId;
}

export function useOzonetelSdkActions({
  callStatusRef,
  isInitializedRef,
  pendingPayloadRef,
  setCallStatus,
  getInitializedSdk,
  prepareAgentAudioForOutboundCall,
  releaseAgentAudio,
  assertSipReady,
}: UseOzonetelSdkActionsParams) {
  const handleSetMode = useCallback(
    async (payload: OztSetModePayload) => {
      return (await getInitializedSdk()).setAgentMode?.(
        payload.mode,
        payload.status,
        payload.reconnect,
      );
    },
    [getInitializedSdk],
  );

  const handleStartCall = useCallback(
    async (payload: OzonetelStartCallPayload) => {
      // if (callStatusRef.current !== 'IDLE') {
      //   throw new Error(`Cannot start call when status is ${callStatusRef.current}`);
      // }

      const sdk = await getInitializedSdk();

      // This check ensures that the softphone is acquired before a call is placed.
      assertSipReady();

      // Inbound calls do not pass through startCall; add a pre-acquire trigger before SDK answer flow when we support them.
      await prepareAgentAudioForOutboundCall();

      try {
        const response = await sdk.initManualCall?.(
          payload.campaignId,
          payload.phoneNumber,
          payload.redial ?? false,
          OZONE_TEL_MANUAL_CALL_UUI,
        );
        setCallStatus('IN_CALL');
        return response;
      } catch (error) {
        releaseAgentAudio();
        throw error;
      }
    },
    [
      // callStatusRef,
      getInitializedSdk,
      prepareAgentAudioForOutboundCall,
      releaseAgentAudio,
      setCallStatus,
      assertSipReady,
    ],
  );

  const handleEndCall = useCallback(async () => {
    // const status = callStatusRef.current;
    // if (!['IN_CALL', 'HOLD', 'MUTE', 'TRANSFER'].includes(status)) {
    //   throw new Error(`Cannot end call when status is ${status}`);
    // }
    try {
      return await (await getInitializedSdk()).hangupCall?.();
    } finally {
      releaseAgentAudio();
      setCallStatus('IDLE');
    }
  }, [
    // callStatusRef,
    getInitializedSdk,
    releaseAgentAudio,
    setCallStatus,
  ]);

  const handleHold = useCallback(
    async (payload: OztPhoneNumberPayload | undefined) => {
      if (callStatusRef.current !== 'IN_CALL') {
        throw new Error(`Cannot hold call when status is ${callStatusRef.current}`);
      }
      const response = await (
        await getInitializedSdk()
      ).holdCall?.(payload?.phoneNumberToHold ?? Number(getCallerId()));
      setCallStatus('HOLD');
      return response;
    },
    [callStatusRef, getInitializedSdk, setCallStatus],
  );

  const handleUnhold = useCallback(
    async (payload: OztPhoneNumberPayload | undefined) => {
      if (callStatusRef.current !== 'HOLD') {
        throw new Error(`Cannot unhold call when status is ${callStatusRef.current}`);
      }
      const response = await (
        await getInitializedSdk()
      ).unHoldCall?.(payload?.phoneNumberToHold ?? Number(getCallerId()));
      setCallStatus('IN_CALL');
      return response;
    },
    [callStatusRef, getInitializedSdk, setCallStatus],
  );

  const handleMute = useCallback(async () => {
    if (callStatusRef.current !== 'IN_CALL') {
      throw new Error(`Cannot mute call when status is ${callStatusRef.current}`);
    }
    const response = await (await getInitializedSdk()).muteCall?.();
    setCallStatus('MUTE');
    return response;
  }, [callStatusRef, getInitializedSdk, setCallStatus]);

  const handleUnmute = useCallback(async () => {
    if (callStatusRef.current !== 'MUTE') {
      throw new Error(`Cannot unmute call when status is ${callStatusRef.current}`);
    }
    const response = await (await getInitializedSdk()).unMuteCall?.();
    setCallStatus('IN_CALL');
    return response;
  }, [callStatusRef, getInitializedSdk, setCallStatus]);

  const handleLogout = useCallback(async () => {
    const sdk = await getInitializedSdk();
    const currentPayload = pendingPayloadRef.current;
    if (!currentPayload) {
      throw new Error('Ozonetel is not initialized');
    }
    try {
      return await sdk.logout?.(currentPayload.customer, currentPayload.agentid);
    } finally {
      releaseAgentAudio();
      isInitializedRef.current = false;
      setCallStatus('UNAUTHORIZED');
    }
  }, [getInitializedSdk, isInitializedRef, pendingPayloadRef, releaseAgentAudio, setCallStatus]);

  const handleReleaseAgent = useCallback(async () => {
    try {
      return await (await getInitializedSdk()).releaseAgent?.(OZONE_TEL_RELEASE_AGENT_MODE);
    } finally {
      releaseAgentAudio();
      setCallStatus('IDLE');
    }
  }, [getInitializedSdk, releaseAgentAudio, setCallStatus]);

  const handleGetCallRecording = useCallback(async () => {
    return await getCallRecordingData(await getInitializedSdk());
  }, [getInitializedSdk]);

  return {
    handleSetMode,
    handleStartCall,
    handleEndCall,
    handleHold,
    handleUnhold,
    handleMute,
    handleUnmute,
    handleLogout,
    handleReleaseAgent,
    handleGetCallRecording,
  } as const;
}
